import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect, { components } from "react-select";
import * as Yup from "yup";

import { getProductListData } from "../../../redux/home/action";
import { getCategory } from "../../../redux/category/action";
import { getUser } from "../../../redux/user/action";
import { getAllBrands } from "../../../redux/brand/action";
import FileUpload from "../../Common/ImageUpload/imageUpload";
import { handleImageUpload } from "../../Common/ImageUpload/handleImageUpload";
import FormField from "../../Common/FormField/FormField";
import { nbSelectStyles } from "../../Common/FormShell/nbSelectStyles";
import "../../Common/FormShell/FormShell.css";

/*
 * Shared coupon form used by AddOffers and UpdateOffers.
 *
 * Coupons only: offer_type is always 2 in the payload — instant offers are
 * retired. Occasion targeting (offer_for=6) is retired too; a legacy offer
 * that still has it must be re-targeted before it can be saved.
 *
 * Every validation message is written for a human: it says what to do, not
 * which rule failed.
 */

const SEARCH_DEBOUNCE_MS = 400;

const APPLIES_TO = [
  { value: 1, label: "Everything in the store" },
  { value: 2, label: "Specific products" },
  { value: 3, label: "Specific categories" },
  { value: 5, label: "Specific brands" },
];

const PLATFORM_OPTIONS = [
  { value: 0, label: "Everywhere (website and app)" },
  { value: 1, label: "Mobile app only" },
  { value: 2, label: "Website only" },
];

export const emptyCouponValues = {
  coupon_code: "",
  name: "",
  description: "",
  discount_type: "",
  discount_value: "",
  max_discount: "",
  offer_for: 1,
  product_ids: [],
  category_ids: [],
  brand_ids: [],
  user_restriction: 1,
  user_ids: [],
  platform_restriction: 0,
  min_order_value: "",
  limit_total_uses: false,
  usage_limit: "",
  limit_per_customer: true,
  usage_per_user: 1,
  start_date: "",
  end_date: "",
  status: 1,
  image: "",
};

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input type="checkbox" checked={props.isSelected} onChange={() => null} />
    <label className="ml-2">{props.label}</label>
  </components.Option>
);

const buildValidationSchema = (isUpdate) =>
  Yup.object({
    coupon_code: Yup.string()
      .required("Please enter the code customers will type at checkout — e.g. APP10.")
      .min(3, "The coupon code needs at least 3 characters.")
      .max(20, "The coupon code can't be longer than 20 characters.")
      .matches(
        /^[A-Za-z0-9]+$/,
        "Coupon codes can only contain letters and numbers — no spaces or symbols."
      )
      .trim(),
    name: Yup.string()
      .required("Please give the coupon a name so your team can recognise it. Any language is fine.")
      .min(3, "The name needs at least 3 characters.")
      .max(50, "The name can't be longer than 50 characters.")
      .trim(),
    description: Yup.string().max(
      500,
      "The internal note can't be longer than 500 characters."
    ),
    discount_type: Yup.number()
      .typeError("Please choose how the discount works.")
      .required("Please choose how the discount works.")
      .oneOf([1, 2], "Please choose how the discount works."),
    discount_value: Yup.number()
      .typeError("Please enter the discount as a number — e.g. 10.")
      .required("Please enter how much the coupon takes off.")
      .positive("The discount must be more than zero.")
      .when("discount_type", {
        is: 1,
        then: (schema) => schema.max(100, "A percentage discount can't be more than 100%."),
      }),
    max_discount: Yup.number()
      .typeError("Please enter the cap as a number — e.g. 50.")
      .positive("The cap must be more than zero.")
      .nullable()
      .transform((value, original) => (original === "" ? null : value)),
    offer_for: Yup.number()
      .required("Please choose what the coupon applies to.")
      .oneOf(
        [1, 2, 3, 5],
        "Occasion targeting is no longer supported — please choose what this coupon applies to."
      ),
    product_ids: Yup.array().when("offer_for", {
      is: 2,
      then: (schema) => schema.min(1, "Please pick at least one product."),
      otherwise: (schema) => schema.notRequired(),
    }),
    category_ids: Yup.array().when("offer_for", {
      is: 3,
      then: (schema) => schema.min(1, "Please pick at least one category."),
      otherwise: (schema) => schema.notRequired(),
    }),
    brand_ids: Yup.array().when("offer_for", {
      is: 5,
      then: (schema) => schema.min(1, "Please pick at least one brand."),
      otherwise: (schema) => schema.notRequired(),
    }),
    user_restriction: Yup.number().oneOf([1, 2]),
    user_ids: Yup.array().when("user_restriction", {
      is: 2,
      then: (schema) =>
        schema.min(
          1,
          "Please pick at least one customer, or switch back to “All customers”."
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    platform_restriction: Yup.number().oneOf([0, 1, 2]),
    min_order_value: Yup.number()
      .typeError("Please enter the minimum order as a number — e.g. 150.")
      .min(0, "The minimum order can't be negative.")
      .nullable()
      .transform((value, original) => (original === "" ? null : value)),
    usage_limit: Yup.number().when("limit_total_uses", {
      is: true,
      then: (schema) =>
        schema
          .typeError("Please enter how many times the coupon can be used in total.")
          .required(
            "Please enter how many times the coupon can be used in total, or untick the box for unlimited."
          )
          .integer("Please use a whole number.")
          .positive("The limit must be at least 1."),
      otherwise: (schema) => schema.notRequired(),
    }),
    usage_per_user: Yup.number().when("limit_per_customer", {
      is: true,
      then: (schema) =>
        schema
          .typeError("Please enter how many times each customer can use the coupon.")
          .required(
            "Please enter how many times each customer can use the coupon, or untick the box for unlimited."
          )
          .integer("Please use a whole number.")
          .positive("The limit must be at least 1."),
      otherwise: (schema) => schema.notRequired(),
    }),
    start_date: isUpdate
      ? Yup.date().required("Please choose the day the coupon starts working.")
      : Yup.date()
          .required("Please choose the day the coupon starts working.")
          .min(
            new Date(new Date().setHours(0, 0, 0, 0)),
            "The start date can't be in the past."
          ),
    end_date: Yup.date()
      .required("Please choose the day the coupon stops working.")
      .min(Yup.ref("start_date"), "The end date must be after the start date."),
    status: Yup.number().oneOf([1, 2]),
  });

const CouponForm = ({ initialValues, onSubmit, loading, isUpdate = false, submitLabel }) => {
  const dispatch = useDispatch();

  const [listParams] = useState({ search_key: "", status: 1, type: 1 });
  const [productParams] = useState({ search_key: "", status: 1, type: "2, 3, 5" });
  const [catParams] = useState({ search_key: "", status: 1 });

  const [selectedFile, setSelectedFile] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  // Selected customers keep their own {value, label} cache so a picked
  // customer stays visible after the search results change underneath.
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const searchTimer = useRef(null);

  const productList = useSelector((state) => state.reusableSlice.productList);
  const categories = useSelector((state) => state.category);
  const brandList = useSelector((state) => state.reusableSlice.brandList);
  const userlist = useSelector(({ user: { userlist } }) => userlist);

  useEffect(() => {
    dispatch(getProductListData(productParams));
  }, [dispatch, productParams]);
  useEffect(() => {
    dispatch(getCategory(catParams));
  }, [dispatch, catParams]);
  useEffect(() => {
    dispatch(getAllBrands(listParams));
  }, [dispatch, listParams]);
  useEffect(() => {
    dispatch(getUser(listParams));
  }, [dispatch, listParams]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => buildValidationSchema(isUpdate), [isUpdate]),
    onSubmit: (values) => onSubmit(buildPayload(values)),
  });

  const buildPayload = (values) => {
    const discountType = Number(values.discount_type);
    const payload = {
      offer_type: 2,
      coupon_code: values.coupon_code.trim().toUpperCase(),
      name: values.name.trim(),
      description: values.description.trim() || null,
      discount_type: discountType,
      discount_value: Number(values.discount_value),
      max_discount:
        discountType === 1 && values.max_discount !== ""
          ? Number(values.max_discount)
          : null,
      offer_for: Number(values.offer_for),
      product_ids: Number(values.offer_for) === 2 ? values.product_ids : [],
      category_ids: Number(values.offer_for) === 3 ? values.category_ids : [],
      brand_ids: Number(values.offer_for) === 5 ? values.brand_ids : [],
      occasion_ids: [],
      user_restriction: Number(values.user_restriction),
      user_ids: Number(values.user_restriction) === 2 ? values.user_ids : [],
      platform_restriction: Number(values.platform_restriction),
      min_order_value:
        values.min_order_value !== "" ? Number(values.min_order_value) : null,
      usage_limit: values.limit_total_uses ? Number(values.usage_limit) : null,
      usage_per_user: values.limit_per_customer ? Number(values.usage_per_user) : null,
      start_date: values.start_date,
      end_date: values.end_date,
      status: Number(values.status),
      image: values.image || null,
    };
    return payload;
  };

  const productOptions = (productList?.products || []).map((item) => ({
    value: item?.id,
    label: item?.name,
  }));
  const categoryOptions = (categories?.categorylist || []).map((item) => ({
    value: item?.id,
    label: item?.title,
  }));
  const brandOptions = (brandList || []).map((item) => ({
    value: item?.id,
    label: item?.name,
  }));

  const customerLabel = (user) => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
    const contact = user?.mobile_number || user?.email_id || "";
    if (fullName && contact) return `${fullName} — ${contact}`;
    return fullName || contact || `Customer #${user?.id}`;
  };

  const searchedCustomerOptions = (userlist?.users || []).map((user) => ({
    value: user.id,
    label: customerLabel(user),
  }));

  // Selected customers first, then search results (minus duplicates).
  const customerOptions = [
    ...selectedCustomers,
    ...searchedCustomerOptions.filter(
      (opt) => !selectedCustomers.some((sel) => sel.value === opt.value)
    ),
  ];

  // When a fresh user list arrives, upgrade any "Customer #id" placeholder
  // labels (prefilled from an existing coupon) to real names.
  useEffect(() => {
    if (!searchedCustomerOptions.length) return;
    setSelectedCustomers((prev) =>
      prev.map((sel) => {
        const match = searchedCustomerOptions.find((opt) => opt.value === sel.value);
        return match && sel.label.startsWith("Customer #") ? match : sel;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userlist]);

  // Prefill the selected-customer chips when editing an existing coupon.
  useEffect(() => {
    if (!isUpdate) return;
    const ids = initialValues.user_ids || [];
    if (!ids.length) return;
    setSelectedCustomers((prev) => {
      if (prev.length) return prev;
      return ids.map((userId) => {
        const match = searchedCustomerOptions.find((opt) => opt.value === userId);
        return match || { value: userId, label: `Customer #${userId}` };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.user_ids, isUpdate]);

  const handleCustomerSearch = (input, { action }) => {
    if (action !== "input-change") return;
    setCustomerSearch(input);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      dispatch(getUser({ ...listParams, search_key: input }));
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleAppliesToChange = (e) => {
    formik.setFieldValue("offer_for", Number(e.target.value));
    formik.setFieldValue("product_ids", []);
    formik.setFieldValue("category_ids", []);
    formik.setFieldValue("brand_ids", []);
  };

  const handleDiscountTypeChange = (e) => {
    const next = Number(e.target.value);
    formik.setFieldValue("discount_type", next);
    if (next === 2) formik.setFieldValue("max_discount", "");
  };

  const isPercentage = Number(formik.values.discount_type) === 1;
  const isLegacyOccasion = Number(formik.values.offer_for) === 6;
  const showSubmitError =
    formik.submitCount > 0 && Object.keys(formik.errors).length > 0;

  const multiSelectProps = {
    isMulti: true,
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
    components: { Option: CheckboxOption },
    styles: nbSelectStyles,
    isSearchable: true,
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="nb-form"
      style={{ maxWidth: 840, margin: "0 auto", padding: "28px 24px 0" }}
    >
      {/* 01 — Coupon */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">01</span>
          <div>
            <h2 className="nb-section__title">Coupon</h2>
            <p className="nb-section__hint">
              The code customers type, and a name so your team can find it.
            </p>
          </div>
        </div>
        <div className="nb-section__body">
          <div className="nb-grid-2">
            <FormField
              name="coupon_code"
              label="Coupon code"
              required
              hint="Letters and numbers only — e.g. APP10. Customers can type it in any case."
              touched={formik.touched.coupon_code}
              error={formik.errors.coupon_code}
            >
              <input
                id="coupon_code"
                name="coupon_code"
                type="text"
                className="nb-input"
                placeholder="e.g. APP10"
                style={{ textTransform: "uppercase" }}
                value={formik.values.coupon_code}
                onChange={(e) =>
                  formik.setFieldValue("coupon_code", e.target.value.toUpperCase())
                }
                onBlur={formik.handleBlur}
              />
            </FormField>
            <FormField
              name="name"
              label="Name"
              required
              hint="For your team, not shown at checkout. English or Arabic — whatever is easiest."
              touched={formik.touched.name}
              error={formik.errors.name}
            >
              <input
                id="name"
                name="name"
                type="text"
                className="nb-input"
                placeholder="e.g. App launch reward / خصم إطلاق التطبيق"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormField>
          </div>
          <FormField
            name="description"
            label="Internal note (optional)"
            hint="Only your team sees this — e.g. what campaign the coupon belongs to."
            touched={formik.touched.description}
            error={formik.errors.description}
          >
            <textarea
              id="description"
              name="description"
              className="nb-input nb-textarea"
              placeholder="e.g. For the August app-download campaign"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </FormField>
        </div>
      </section>

      {/* 02 — Discount */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">02</span>
          <div>
            <h2 className="nb-section__title">Discount</h2>
            <p className="nb-section__hint">How much the coupon takes off.</p>
          </div>
        </div>
        <div className="nb-section__body">
          <div className="nb-grid-2">
            <FormField
              name="discount_type"
              label="Discount type"
              required
              touched={formik.touched.discount_type}
              error={formik.errors.discount_type}
            >
              <select
                id="discount_type"
                className="nb-input"
                onChange={handleDiscountTypeChange}
                onBlur={formik.handleBlur}
                value={formik.values.discount_type}
              >
                <option value="">Choose a type…</option>
                <option value="1">Percentage off (%)</option>
                <option value="2">Fixed amount off (SAR)</option>
              </select>
            </FormField>
            <FormField
              name="discount_value"
              label={isPercentage ? "Percentage" : "Amount"}
              required
              hint={
                isPercentage
                  ? "e.g. 10 means 10% off"
                  : "e.g. 20 means 20 SAR off the order"
              }
              touched={formik.touched.discount_value}
              error={formik.errors.discount_value}
            >
              <input
                type="number"
                name="discount_value"
                className="nb-input"
                placeholder={isPercentage ? "e.g. 10" : "e.g. 20"}
                value={formik.values.discount_value}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
              />
            </FormField>
          </div>
          {isPercentage && (
            <FormField
              name="max_discount"
              label="Cap the discount at (optional)"
              hint='For "10% off up to 50 SAR", enter 50. Leave empty for no cap.'
              touched={formik.touched.max_discount}
              error={formik.errors.max_discount}
            >
              <input
                type="number"
                name="max_discount"
                className="nb-input"
                placeholder="e.g. 50"
                value={formik.values.max_discount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
              />
            </FormField>
          )}
        </div>
      </section>

      {/* 03 — What it applies to */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">03</span>
          <div>
            <h2 className="nb-section__title">What it applies to</h2>
            <p className="nb-section__hint">
              The coupon only discounts these items; the rest of the cart is
              charged normally.
            </p>
          </div>
        </div>
        <div className="nb-section__body">
          <FormField
            name="offer_for"
            label="Applies to"
            touched={formik.touched.offer_for}
            error={formik.errors.offer_for}
          >
            <select
              name="offer_for"
              className="nb-input"
              value={formik.values.offer_for}
              onChange={handleAppliesToChange}
            >
              {APPLIES_TO.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {isLegacyOccasion && (
                <option value={6} disabled>
                  Occasions (no longer supported)
                </option>
              )}
            </select>
          </FormField>

          {isLegacyOccasion && (
            <p className="ff-field__hint" role="alert">
              This coupon was targeted at occasions, which the store no longer
              uses. Please choose what it should apply to now.
            </p>
          )}

          {Number(formik.values.offer_for) === 2 && (
            <FormField
              name="product_ids"
              label="Products"
              required
              touched={formik.touched.product_ids}
              error={formik.errors.product_ids}
            >
              <ReactSelect
                {...multiSelectProps}
                value={productOptions.filter((o) =>
                  formik.values.product_ids.includes(o.value)
                )}
                onChange={(selected) =>
                  formik.setFieldValue(
                    "product_ids",
                    (selected || []).map((item) => item.value)
                  )
                }
                options={productOptions}
                placeholder="Search and pick products…"
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "No products match that search" : "Type to search products"
                }
              />
            </FormField>
          )}

          {Number(formik.values.offer_for) === 3 && (
            <FormField
              name="category_ids"
              label="Categories"
              required
              touched={formik.touched.category_ids}
              error={formik.errors.category_ids}
            >
              <ReactSelect
                {...multiSelectProps}
                value={categoryOptions.filter((o) =>
                  formik.values.category_ids.includes(o.value)
                )}
                onChange={(selected) =>
                  formik.setFieldValue(
                    "category_ids",
                    (selected || []).map((item) => item.value)
                  )
                }
                options={categoryOptions}
                placeholder="Pick categories…"
              />
            </FormField>
          )}

          {Number(formik.values.offer_for) === 5 && (
            <FormField
              name="brand_ids"
              label="Brands"
              required
              touched={formik.touched.brand_ids}
              error={formik.errors.brand_ids}
            >
              <ReactSelect
                {...multiSelectProps}
                value={brandOptions.filter((o) =>
                  formik.values.brand_ids.includes(o.value)
                )}
                onChange={(selected) =>
                  formik.setFieldValue(
                    "brand_ids",
                    (selected || []).map((item) => item.value)
                  )
                }
                options={brandOptions}
                placeholder="Pick brands…"
              />
            </FormField>
          )}
        </div>
      </section>

      {/* 04 — Who can use it */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">04</span>
          <div>
            <h2 className="nb-section__title">Who can use it</h2>
            <p className="nb-section__hint">
              Open to everyone, or a private coupon for chosen customers.
            </p>
          </div>
        </div>
        <div className="nb-section__body">
          <FormField name="user_restriction" label="Customers">
            <select
              name="user_restriction"
              className="nb-input"
              value={formik.values.user_restriction}
              onChange={(e) => {
                formik.setFieldValue("user_restriction", Number(e.target.value));
                if (Number(e.target.value) === 1) {
                  formik.setFieldValue("user_ids", []);
                  setSelectedCustomers([]);
                }
              }}
            >
              <option value={1}>All customers</option>
              <option value={2}>Only specific customers</option>
            </select>
          </FormField>

          {Number(formik.values.user_restriction) === 2 && (
            <FormField
              name="user_ids"
              label="Customers"
              required
              hint="Search by name, mobile number, or email."
              touched={formik.touched.user_ids}
              error={formik.errors.user_ids}
            >
              <ReactSelect
                {...multiSelectProps}
                value={selectedCustomers}
                onChange={(selected) => {
                  setSelectedCustomers(selected || []);
                  formik.setFieldValue(
                    "user_ids",
                    (selected || []).map((item) => item.value)
                  );
                }}
                options={customerOptions}
                inputValue={customerSearch}
                onInputChange={handleCustomerSearch}
                filterOption={() => true}
                placeholder="Search customers…"
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "No customers match that search" : "Type to search customers"
                }
              />
            </FormField>
          )}
        </div>
      </section>

      {/* 05 — Where it works */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">05</span>
          <div>
            <h2 className="nb-section__title">Where it works</h2>
            <p className="nb-section__hint">
              Limit the coupon to one sales channel — e.g. an app-only reward.
            </p>
          </div>
        </div>
        <div className="nb-section__body">
          <FormField
            name="platform_restriction"
            label="Channel"
            hint={
              Number(formik.values.platform_restriction) === 1
                ? "Customers on the website will see: “This coupon can only be used on our mobile app.”"
                : Number(formik.values.platform_restriction) === 2
                ? "Customers on the app will see: “This coupon can only be used on our website.”"
                : undefined
            }
          >
            <select
              name="platform_restriction"
              className="nb-input"
              value={formik.values.platform_restriction}
              onChange={(e) =>
                formik.setFieldValue("platform_restriction", Number(e.target.value))
              }
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </section>

      {/* 06 — Rules & limits */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">06</span>
          <div>
            <h2 className="nb-section__title">Rules &amp; limits</h2>
            <p className="nb-section__hint">
              Optional guardrails — leave anything empty or unticked for no limit.
            </p>
          </div>
        </div>
        <div className="nb-section__body">
          <FormField
            name="min_order_value"
            label="Minimum order to use it (optional)"
            hint="The cart total must reach this before the coupon works. Leave empty for no minimum."
            touched={formik.touched.min_order_value}
            error={formik.errors.min_order_value}
          >
            <input
              type="number"
              name="min_order_value"
              className="nb-input"
              placeholder="e.g. 150"
              value={formik.values.min_order_value}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onWheel={(e) => e.target.blur()}
            />
          </FormField>

          <div className="nb-grid-2">
            <FormField
              name="usage_limit"
              label={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={formik.values.limit_total_uses}
                    onChange={(e) => {
                      formik.setFieldValue("limit_total_uses", e.target.checked);
                      if (!e.target.checked) formik.setFieldValue("usage_limit", "");
                    }}
                  />
                  Limit total uses
                </span>
              }
              hint="Across all customers. Unticked = unlimited."
              touched={formik.touched.usage_limit}
              error={formik.values.limit_total_uses ? formik.errors.usage_limit : undefined}
            >
              <input
                type="number"
                name="usage_limit"
                className="nb-input"
                placeholder="e.g. 100"
                disabled={!formik.values.limit_total_uses}
                value={formik.values.usage_limit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
              />
            </FormField>

            <FormField
              name="usage_per_user"
              label={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={formik.values.limit_per_customer}
                    onChange={(e) => {
                      formik.setFieldValue("limit_per_customer", e.target.checked);
                      if (!e.target.checked) formik.setFieldValue("usage_per_user", "");
                    }}
                  />
                  Limit uses per customer
                </span>
              }
              hint="How many times one customer can use it. Unticked = unlimited."
              touched={formik.touched.usage_per_user}
              error={
                formik.values.limit_per_customer ? formik.errors.usage_per_user : undefined
              }
            >
              <input
                type="number"
                name="usage_per_user"
                className="nb-input"
                placeholder="e.g. 1"
                disabled={!formik.values.limit_per_customer}
                value={formik.values.usage_per_user}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
              />
            </FormField>
          </div>
        </div>
      </section>

      {/* 07 — Schedule & status */}
      <section className="nb-section">
        <div className="nb-section__head">
          <span className="nb-section__step">07</span>
          <div>
            <h2 className="nb-section__title">Schedule &amp; status</h2>
            <p className="nb-section__hint">When the coupon is live.</p>
          </div>
        </div>
        <div className="nb-section__body">
          <div className="nb-grid-2">
            <FormField
              name="start_date"
              label="Start date"
              required
              touched={formik.touched.start_date}
              error={formik.errors.start_date}
            >
              <input
                type="date"
                name="start_date"
                className="nb-input"
                value={formik.values.start_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormField>
            <FormField
              name="end_date"
              label="End date"
              required
              touched={formik.touched.end_date}
              error={formik.errors.end_date}
            >
              <input
                type="date"
                name="end_date"
                className="nb-input"
                value={formik.values.end_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormField>
          </div>

          <div className="nb-grid-2">
            <FormField name="status" label="Status">
              <select
                name="status"
                className="nb-input"
                value={formik.values.status}
                onChange={(e) => formik.setFieldValue("status", Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={2}>Inactive</option>
              </select>
            </FormField>
          </div>

          <FormField
            name="image"
            label="Banner image (optional)"
            hint="Shown at the top of the coupon card in the app. Recommended size: 1200 × 480 px (wide, 2.5:1), JPG or PNG under 500 KB. Keep text and logos near the centre — the edges get cropped on larger phones."
            touched={formik.touched.image}
            error={formik.errors.image}
          >
            <FileUpload
              files={selectedFile}
              setFiles={setSelectedFile}
              formik={formik}
              prop={{ title: "", name: "image" }}
              onFileSelect={async (e) => {
                try {
                  const file = e.target.files[0];
                  if (!file) return;
                  await handleImageUpload(
                    file,
                    { title: "images", name: "image" },
                    "image",
                    formik
                  );
                  setSelectedFile(file);
                } catch (error) {
                  console.error(error);
                }
              }}
            />
            {formik.values.image ? (
              <div
                style={{
                  marginTop: 10,
                  width: "100%",
                  maxWidth: 500,
                  aspectRatio: "2.5 / 1",
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Preview crops exactly like the app card (cover, 2.5:1). */}
                <img
                  src={formik.values.image}
                  alt="Coupon banner preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            ) : null}
          </FormField>
        </div>
      </section>

      {showSubmitError && (
        <p className="ff-field__error" role="alert" style={{ textAlign: "right" }}>
          Some fields need attention — please fix the messages highlighted above.
        </p>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "8px 0 40px",
        }}
      >
        <button
          type="button"
          className="nb-btn nb-btn--ghost"
          onClick={() => window.history.back()}
        >
          Cancel
        </button>
        <button type="submit" className="nb-btn nb-btn--primary" disabled={loading}>
          {loading ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default CouponForm;
